import { FC, useEffect } from 'react';
import { Form, message, Typography } from 'antd';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { CodeField, FormItem } from 'componets/form';
import { ModalComponent } from 'componets/common';

import { ModalProps } from 'models';
import { CODE_LENGTH, Rules } from 'utils/constants';
import { AppDispatch } from 'store';
import { useAppSelector, useCodeForm } from 'hooks';
import { approveWithdrawalRequest, getWithdrawals } from 'features/funding/actions';
import { getBalance } from 'features/overview/actions';
import { selectUser } from 'selectors';

interface Withdrawal2FAProps extends ModalProps {
	uuid: string;
}

export const Withdrawal2FA: FC<Withdrawal2FAProps> = ({ isOpen, onClose, uuid }) => {
	const { t } = useTranslation();
	const dispatch = useDispatch<AppDispatch>();

	const user = useAppSelector(selectUser);

	const { fields, disabled, setFields, setIsValid, isValid, setDisabled, code } = useCodeForm();

	useEffect(() => {
		if (isValid) {
			setDisabled(true);
			dispatch(approveWithdrawalRequest({ uuid, code: code.value }))
				.unwrap()
				.then(() => {
					onClose();
					dispatch(getBalance());
				})
				.catch((e) => {
					if (e.message === 'Code doesn`t match') {
						const count = Number(e.errors.countTries);
						const text = t(
							count > 1 ? 'wrongCodeWithdrawalAttemptsText' : 'wrongCodeWithdrawalAttemptText',
						);

						setFields([
							{
								...code,
								errors: [t(text, { count })],
							},
						]);

						if (count === 0) {
							message.warn(t('wrongCodeZeroAttemptText'));
							dispatch(getWithdrawals());
							onClose();
						}
					} else if (e.message === 'Withdrawal request status is cancelled') {
						setFields([
							{
								...code,
								errors: [t('withdrawRequestsStatusCanceledText')],
							},
						]);
					} else {
						setFields([
							{
								...code,
								errors: [t('errorInvalidCodeText')],
							},
						]);
					}

					setDisabled(false);
					setIsValid(false);
				});
		}
	}, [code, dispatch, isValid, setDisabled, setFields, setIsValid]);

	return (
		<ModalComponent isOpen={isOpen} onClose={onClose} title={t('withdrawalConfirmationText')}>
			<div className="modal-content">
				<Typography.Text className="modal-enterCode" type="secondary">
					{t('verifyEmailLabelText')} <Typography.Text>{user.email}</Typography.Text>
				</Typography.Text>
				<Form onFieldsChange={setFields} fields={fields} className="modal-form">
					<FormItem className="code" name="code" rules={[Rules.REQUIRED]}>
						<CodeField
							name="code"
							fields={CODE_LENGTH}
							disabled={disabled}
							autoComplete="one-time-code"
						/>
					</FormItem>
				</Form>
			</div>
		</ModalComponent>
	);
};
